import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 30; // ~75 s ceiling

export default function CheckoutSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id");
  const [state, setState] = useState("polling"); // polling | done | error
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);
  const firedConfetti = useRef(false);

  useEffect(() => {
    if (!orderId) {
      setState("error");
      return;
    }
    let cancelled = false;

    async function poll() {
      try {
        const res = await api.confirmOrder(orderId);
        if (cancelled) return;
        setOrder(res.order || null);
        if (res.status === "succeeded" && res.payment_status === "paid") {
          setState("done");
          if (!firedConfetti.current) {
            firedConfetti.current = true;
            confetti({
              particleCount: 200, spread: 120, origin: { y: 0.35 },
              colors: ["#4361EE", "#FFB703", "#F72585", "#4CC9F0", "#E07A5F"],
            });
          }
          return;
        }
        if (res.status === "failed" || res.status === "cancelled") {
          setState("error");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (cancelled) return;
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    }

    poll();
    return () => { cancelled = true; };
  }, [orderId]);

  const continueHref = order?.course_id ? `/courses/${order.course_id}` : "/dashboard";

  return (
    <div className="min-h-screen bg-[#FBF7EE] flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-[2rem] bg-white border-2 border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.10)] overflow-hidden" data-testid="checkout-success-page">
        <div className="p-8 bg-gradient-to-br from-[#DCFCE7] to-[#DFF6FD] text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-white text-[#166534] grid place-items-center shadow">
            {state === "polling" ? (
              <Loader2 className="h-8 w-8 animate-spin" />
            ) : state === "done" ? (
              <CheckCircle2 className="h-9 w-9" />
            ) : (
              <Sparkles className="h-8 w-8 text-[#7A4B00]" />
            )}
          </div>
          <h1 className="mt-4 text-3xl font-black tracking-tight" data-testid="checkout-success-title">
            {state === "done" ? t("unlock.checkoutSuccessTitle") : t("unlock.processing")}
          </h1>
          <p className="mt-2 text-sm font-semibold text-black/70">
            {state === "done" ? t("unlock.checkoutSuccessDone") : t("unlock.checkoutSuccessBody")}
          </p>
        </div>

        <div className="p-6 space-y-3">
          {state === "error" && (
            <div className="rounded-2xl bg-[#FEE2E2] text-[#991B1B] px-4 py-3 text-sm font-semibold" data-testid="checkout-success-error">
              {t("unlock.errorGeneric")}
            </div>
          )}

          {order?.order_id && (
            <div className="text-xs font-bold text-black/40 text-center">
              {t("unlock.orderId")}: <span data-testid="checkout-success-order-id">{order.order_id}</span>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2">
            <Link
              to={continueHref}
              className="w-full rounded-full bg-[#1A1A1A] text-white font-bold px-6 py-3 shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition inline-flex items-center justify-center gap-2"
              data-testid="checkout-success-continue"
            >
              <Sparkles className="h-4 w-4" /> {t("unlock.checkoutTakeMeBack")}
            </Link>
            {state === "error" && (
              <button
                onClick={() => { attempts.current = 0; setState("polling"); /* trigger poll via effect via navigate replace */ navigate(0); }}
                className="w-full rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-4 py-3 text-sm transition"
                data-testid="checkout-success-retry"
              >
                {t("unlock.checkoutCheckAgain")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
